import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/Auth/authContext';
import { useClientAccess } from '../../../hooks/useClientAccess';
import { useI18n } from '../../../contexts/I18nContext';
import { apiClient } from '../../../services/apiClient';
import { clientService } from '../../../services/clientService';
import { ANEP_CLIENT_ID, ConfigTemplateListItem, isAnepTemplate } from '../../../types/configTemplate';
import { Client } from '../../../types/client';
import { isAbortError } from '../../../utils/abortError';
import { SortDirection, nextSort } from '../../common/SortableHeader';

export type TemplateSortField = 'client_id' | 'device_type' | 'name' | 'device_count' | 'updated_at';

/**
 * The templates list: the page the API returned for the current search,
 * owner and type filters, the fleet figures from the stats endpoint, and
 * the client names for the owner column. The sort applies to the page.
 */
export const useTemplatesList = (refreshTrigger?: number) => {
  const { isAuthenticated } = useAuth();
  const { isAnepStaff } = useClientAccess();
  const canSeeInactive = isAnepStaff;
  const { t } = useI18n();
  const axios = useMemo(() => apiClient.getAxiosInstance(), []);

  const [templates, setTemplates] = useState<ConfigTemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localRefresh, setLocalRefresh] = useState(0);

  // Filters. clientFilter is an owner id: a client UUID, ANEP_CLIENT_ID for ANEP, '' for everyone.
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [deviceTypeFilter, setDeviceTypeFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Sort and page
  const [sort, setSort] = useState<{ field: TemplateSortField; direction: SortDirection }>({ field: 'updated_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [serverTotalItems, setServerTotalItems] = useState(0);
  const [serverTotalPages, setServerTotalPages] = useState(0);

  // Fleet-wide figures (the list only holds one page)
  const [stats, setStats] = useState<{ total: number; active: number; types: number } | null>(null);
  const [clientList, setClientList] = useState<Client[]>([]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm), 250);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, deviceTypeFilter, clientFilter, showInactive]);

  // The page: search, owner, type and inactive flag are applied by the API.
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const params: Record<string, any> = { page: currentPage, limit: itemsPerPage };
        if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
        if (deviceTypeFilter) params.device_type = deviceTypeFilter;
        if (clientFilter) params.client_id = clientFilter;
        if (showInactive && canSeeInactive) params.include_inactive = true;
        const response = await axios.get('/v2/config-templates', { params, signal: ac.signal });
        const items: ConfigTemplateListItem[] = response.data.items.map((tmpl: any) => ({
          id: String(tmpl.id),
          name: tmpl.name,
          description: tmpl.description,
          client_id: tmpl.client_id || ANEP_CLIENT_ID,
          device_type: tmpl.device_type,
          is_default: tmpl.is_default === true,
          device_count: tmpl.device_count ?? 0,
          is_active: tmpl.is_active,
          created_at: tmpl.created_at,
          updated_at: tmpl.updated_at,
        }));
        setTemplates(items);
        setServerTotalItems(response.data.total ?? items.length);
        setServerTotalPages(response.data.pages ?? 1);
      } catch (err: any) {
        if (isAbortError(err)) return;
        console.error('[CONFIG TEMPLATES TABLE] Error fetching templates:', err);
        setTemplates([]);
        setServerTotalItems(0);
        setServerTotalPages(0);
        const status = err.response?.status;
        setError(status === 404 || status === 500 ? t('configTemplates.errorNotInitialised') : err.message || t('configTemplates.errorLoading'));
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [isAuthenticated, refreshTrigger, localRefresh, axios, showInactive, canSeeInactive, currentPage, itemsPerPage, debouncedSearch, deviceTypeFilter, clientFilter, t]);

  // The figures are best effort: the table works without them.
  useEffect(() => {
    if (!isAuthenticated) return;
    const ac = new AbortController();
    axios.get('/v2/config-templates/stats', { signal: ac.signal })
      .then((res) => setStats({
        total: res.data?.total_templates ?? 0,
        active: res.data?.active_templates ?? 0,
        types: Object.keys(res.data?.templates_by_type ?? {}).length,
      }))
      .catch((e: any) => { if (!isAbortError(e)) console.warn('[CONFIG TEMPLATES TABLE] stats fetch failed:', e?.message || e); });
    return () => ac.abort();
  }, [isAuthenticated, refreshTrigger, localRefresh, axios]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    clientService.fetchAllClients()
      .then((clients) => { if (!cancelled) setClientList(clients); })
      .catch((err) => { if (!cancelled) console.error('[CONFIG TEMPLATES TABLE] Error fetching clients:', err); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  /** "ANEP" for an ANEP template, the client's name, or null when unknown. */
  const ownerName = useCallback((clientId: string): string | null => {
    if (isAnepTemplate(clientId)) return t('configTemplates.anep');
    return clientList.find((c) => c.id === clientId)?.name || null;
  }, [clientList, t]);

  const sorted = useMemo(() => {
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...templates].sort((a, b) => {
      if (sort.field === 'client_id') return (ownerName(a.client_id) ?? '').localeCompare(ownerName(b.client_id) ?? '') * dir;
      if (sort.field === 'device_count') return (a.device_count - b.device_count) * dir;
      return String(a[sort.field] ?? '').localeCompare(String(b[sort.field] ?? '')) * dir;
    });
  }, [templates, sort, ownerName]);

  const facts = useMemo(() => ({
    total: stats?.total ?? null,
    active: stats?.active ?? null,
    inactive: stats ? Math.max(0, stats.total - stats.active) : null,
    types: stats?.types ?? null,
  }), [stats]);

  const maxDevices = useMemo(() => templates.reduce((m, x) => Math.max(m, x.device_count), 0), [templates]);
  const hasFilters = Boolean(clientFilter || deviceTypeFilter);

  const clearFilters = () => {
    setClientFilter('');
    setDeviceTypeFilter('');
    setCurrentPage(1);
  };

  return {
    isAnepStaff, canSeeInactive,
    templates, sorted, loading, error, facts, maxDevices, clientList, ownerName,
    searchTerm, setSearchTerm, clientFilter, setClientFilter, deviceTypeFilter, setDeviceTypeFilter, showInactive, setShowInactive, hasFilters, clearFilters,
    sort, sortBy: (field: TemplateSortField) => setSort((s) => nextSort(s, field, ['device_count', 'updated_at'])),
    currentPage, setCurrentPage, itemsPerPage, setItemsPerPage, serverTotalItems, serverTotalPages,
    reload: () => setLocalRefresh((n) => n + 1),
  };
};

export type TemplatesListState = ReturnType<typeof useTemplatesList>;
